import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchHeatmapDetail } from '../api/skillDevelopment';
import type { HeatmapDetailEntry } from '../types';

export default function CategorySkillDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const groupBy = searchParams.get('groupBy') ?? 'department';
  const group = searchParams.get('group') ?? '';
  const category = searchParams.get('category') ?? '';

  const [data, setData] = useState<HeatmapDetailEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!group || !category) return;
    let cancelled = false;
    setLoading(true);
    fetchHeatmapDetail(groupBy, group, category)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupBy, group, category]);

  // Build matrix: employees as rows, skills as columns
  const { employees, skills, lookup } = useMemo(() => {
    const empMap = new Map<string, string>();
    const skillSet = new Set<string>();
    const lk = new Map<string, number>();

    for (const entry of data) {
      empMap.set(entry.employee_id, entry.employee_name);
      skillSet.add(entry.skill_name);
      lk.set(`${entry.employee_id}|${entry.skill_name}`, entry.proficiency);
    }

    const emps = [...empMap.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const sk = [...skillSet].sort();

    return { employees: emps, skills: sk, lookup: lk };
  }, [data]);

  const groupByLabel =
    groupBy === 'jobTitle' ? t('skillDev.jobTitle', 'Job Title') :
    groupBy === 'officeLocation' ? t('skillDev.officeLocation', 'Office Location') :
    t('skillDev.department', 'Department');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/skill-development')}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          &larr; {t('common.back', 'Back')}
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {category}
          </h1>
          <p className="text-sm text-gray-500">
            {groupByLabel}: {group}
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12" role="status">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
          <span className="sr-only">{t('common.loading', 'Loading...')}</span>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          {t('common.error', 'Error')}: {error}
        </div>
      )}

      {!loading && !error && employees.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
          {t('skillDev.noDetailData', 'No employee skill data found for this selection.')}
        </div>
      )}

      {!loading && !error && employees.length > 0 && (
        <div className="rounded-lg border border-gray-200" style={{ overflow: 'auto', maxWidth: 'calc(100vw - 20rem)' }}>
          <table className="w-max divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500 sticky left-0 bg-gray-50 z-10 min-w-[180px]">
                  {t('skillDev.employee', 'Employee')}
                </th>
                {skills.map((skill) => (
                  <th key={skill} className="px-1 pt-2 pb-3 text-center font-medium text-gray-500 align-bottom" style={{ height: '140px' }}>
                    <div className="flex items-end justify-center h-full">
                      <span className="whitespace-nowrap text-xs" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                        {skill}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {employees.map((emp) => (
                <tr key={emp.id}>
                  <td className="px-4 py-3 font-medium text-gray-900 sticky left-0 bg-white z-10 min-w-[180px]">
                    {emp.name}
                  </td>
                  {skills.map((skill) => {
                    const val = lookup.get(`${emp.id}|${skill}`);
                    return (
                      <td key={skill} className="px-3 py-3 text-center">
                        {val != null ? (
                          <span
                            className={[
                              'inline-flex items-center justify-center w-10 h-10 rounded-lg text-sm font-bold',
                              val >= 4 ? 'bg-green-100 text-green-800' :
                              val >= 3 ? 'bg-blue-100 text-blue-800' :
                              val >= 2 ? 'bg-amber-100 text-amber-800' :
                              'bg-red-100 text-red-800',
                            ].join(' ')}
                            title={`${emp.name} - ${skill}: ${val}`}
                          >
                            {val}
                          </span>
                        ) : (
                          <span className="text-gray-300" aria-label={t('skillDev.noData', 'No data')}>-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
